import React, { useState, useEffect } from "react";
import { AdminFunction, ZoneDto, FunctionCategoryDto } from "./types";
import "./AdminEvent.css";

interface FunctionFormProps {
    func: AdminFunction | null;
    eventID: number;
    zones: ZoneDto[];
    categories: FunctionCategoryDto[];
    onSave: (func: AdminFunction & { assignedZones?: ZoneDto[] }) => void;
    onCancel: () => void;
}

const FunctionForm: React.FC<FunctionFormProps> = ({
    func,
    eventID,
    zones,
    categories,
    onSave,
    onCancel
}) => {
    const [formData, setFormData] = useState<AdminFunction & { assignedZones?: ZoneDto[] }>({
        functionID: undefined,
        functionName: "",
        description: "",
        eventID,
        functionCategoryId: undefined,
        useDesign2: false,
        assignedZones: [],
    });

    useEffect(() => {
        if (func) {
            setFormData({
                functionID: func.functionID,
                functionName: func.functionName || "",
                description: func.description || "",
                eventID: func.eventID,
                functionCategoryId: func.functionCategoryId,
                useDesign2: func.useDesign2 ?? false,
                assignedZones: func.assignedZones || [],
                categoryName: func.categoryName
            });
        } else {
            setFormData({
                functionID: undefined,
                functionName: "",
                description: "",
                eventID,
                functionCategoryId: undefined,
                useDesign2: false,
                assignedZones: [],
            });
        }
    }, [func, eventID]);

    const handleZoneToggle = (zone: ZoneDto) => {
        setFormData(prev => {
            const assigned = prev.assignedZones ?? [];
            const exists = assigned.some(z => z.zoneID === zone.zoneID);
            return {
                ...prev,
                assignedZones: exists
                    ? assigned.filter(z => z.zoneID !== zone.zoneID)
                    : [...assigned, zone],
            };
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.functionName.trim()) {
            alert("Funktion navn er påkrævet");
            return;
        }

        if (!formData.functionCategoryId) {
            alert("Kategori er påkrævet");
            return;
        }

        onSave(formData);
    };

    return (
        <div className="function-modal-overlay">
            <div className="function-modal">

                <div className="eventform-page">
                    <form onSubmit={handleSubmit} className="eventform-card">

                        <div className="eventform-header">
                            <h2 className="eventform-title">
                                {formData.functionID ? "Opdater Funktion" : "Opret Funktion"}
                            </h2>
                            <p className="eventform-subtitle">
                                Opret/rediger funktion, vælg kategori og tilknyttede zones.
                            </p>
                        </div>

                        <div className="eventform-grid">
                            <div className="field">
                                <label>Funktion Navn</label>
                                <input
                                    type="text"
                                    value={formData.functionName}
                                    onChange={(e) =>
                                        setFormData({ ...formData, functionName: e.target.value })
                                    }
                                    required
                                />
                            </div>

                            <div className="field">
                                <label>Kategori</label>
                                <select
                                    className="eventform-select"
                                    value={formData.functionCategoryId ?? ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            functionCategoryId: e.target.value
                                                ? Number(e.target.value)
                                                : undefined
                                        })
                                    }
                                    required
                                >
                                    <option value="">Vælg kategori</option>
                                    {categories.map((cat) => (
                                        <option
                                            key={cat.functionCategoryId}
                                            value={cat.functionCategoryId}
                                        >
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Beskrivelse</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) =>
                                        setFormData({ ...formData, description: e.target.value })
                                    }
                                    rows={3}
                                />
                            </div>

                            {/* KORTDESIGN FJERNET */}

                            <div className="field zones-field">
                                <label>Vælg Zones</label>

                                <div className="zone-checkbox-grid zone-checkbox-grid-card">
                                    {zones.map((zone) => (
                                        <label
                                            key={zone.zoneID}
                                            className="zone-checkbox-item zone-item"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    formData.assignedZones?.some(
                                                        (z) => z.zoneID === zone.zoneID
                                                    ) ?? false
                                                }
                                                onChange={() => handleZoneToggle(zone)}
                                            />

                                            <span className="zone-pill">
                                                <strong>{zone.number}</strong>
                                                <span className="zone-name">
                                                    {zone.zoneName}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="eventform-divider" />

                        <div className="eventform-actions">
                            <div />
                            <div className="actions-right">
                                <button type="submit" className="btn btn-primary">
                                    Gem
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={onCancel}
                                >
                                    Annuller
                                </button>
                            </div>
                        </div>

                    </form>
                </div>

            </div>
        </div>
    );
};

export default FunctionForm;